import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import {
  AddReactionRequest,
  ReactionType,
  StoryDetail as StoryDetailData,
  SharedWithPerson,
} from '../../../../core/models/story.models';

// ── Reaction picker definition ─────────────────────────────────────────────────
// Displayed as clickable emoji buttons above the reaction summary list.
const REACTION_PICKER = [
  { type: ReactionType.Heart, emoji: '❤️', label: 'Heart' },
  { type: ReactionType.Smile, emoji: '😊', label: 'Smile' },
  { type: ReactionType.Wow,   emoji: '😮', label: 'Wow'   },
  { type: ReactionType.Sad,   emoji: '😢', label: 'Sad'   },
] as const;

/** Maps a ReactionType number to its display emoji. */
const REACTION_EMOJI: Record<number, string> = {
  [ReactionType.Heart]: '❤️',
  [ReactionType.Smile]: '😊',
  [ReactionType.Wow]:   '😮',
  [ReactionType.Sad]:   '😢',
};

@Component({
  selector: 'app-story-detail',
  templateUrl: './story-detail.html',
  styleUrl: './story-detail.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
})
export class StoryDetail implements OnInit {

  // ── Dependency Injection ───────────────────────────────────────────────────
  private readonly api        = inject(ApiService);
  private readonly route      = inject(ActivatedRoute);
  private readonly location   = inject(Location);
  private readonly destroyRef = inject(DestroyRef);

  // ── Page-level UI state ─────────────────────────────────────────────────────

  /** True when the story detail API call returned an error. */
  readonly hasError = signal(false);

  /** The loaded story detail. Null until the API responds successfully. */
  readonly story = signal<StoryDetailData | null>(null);

  /** True while an add-/remove-reaction API call is in flight. */
  readonly isReacting = signal(false);

  /** Controls the Full Transcript section visibility. */
  readonly transcriptOpen = signal(true);

  /** Controls the secondary reaction picker (Wow / Sad) open state. */
  readonly isReactionPickerOpen = signal(false);

  // ── Exposed constants for the template ────────────────────────────────────
  readonly reactionPicker = REACTION_PICKER;

  // ── Derived / computed values ──────────────────────────────────────────────

  /**
   * The reactionType the current user has applied to this story, or null.
   * Derived from the reactions array where isCurrentUser === true.
   */
  readonly currentUserReaction = computed<number | null>(() => {
    const reaction = this.story()?.reactions.find(r => r.isCurrentUser);
    return reaction?.reactionType ?? null;
  });

  /**
   * Reactions grouped by type for the summary display.
   * Each entry: { type, emoji, names[] } where names uses "You" for the
   * current user and the actual userName for everyone else.
   */
  readonly reactionGroups = computed(() => {
    const reactions = this.story()?.reactions ?? [];
    const groups = new Map<number, { emoji: string; names: string[] }>();

    for (const r of reactions) {
      if (!groups.has(r.reactionType)) {
        groups.set(r.reactionType, {
          emoji: REACTION_EMOJI[r.reactionType] ?? '👍',
          names: [],
        });
      }
      groups.get(r.reactionType)!.names.push(r.isCurrentUser ? 'You' : r.userName);
    }

    return Array.from(groups.entries()).map(([type, val]) => ({ type, ...val }));
  });

  // ── Private ────────────────────────────────────────────────────────────────

  /** Route `:id` param — updated reactively via paramMap subscription. */
  private storyId = '';

  // ──────────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    /**
     * Subscribe to paramMap instead of using snapshot.
     * This ensures the component re-loads data when Angular reuses the
     * same component instance during in-app navigation between related
     * stories (e.g. /app/stories/1 → /app/stories/2).
     */
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        this.storyId = params.get('id') ?? '';
        // Reset state so previous story doesn’t flash while the new one loads
        this.story.set(null);
        this.hasError.set(false);
        this.isReactionPickerOpen.set(false);
        this.loadStory();
      });
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Data loading
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Fetches the story detail from the API.
   * The global loader (ApiService showLoader) handles the loading indicator.
   * @param silent  When true, suppresses the global loader and error reset
   *                (used for quiet post-reaction refreshes).
   */
  async loadStory(silent = false): Promise<void> {
    if (!this.storyId) return;

    if (!silent) {
      this.hasError.set(false);
    }

    try {
      const resp = await this.api.request<StoryDetailData>(
        'GET',
        `${API_ROUTES.GET_STORY_DETAIL}/${this.storyId}`,
        undefined,
        // showLoader:true delegates the spinner to the global loader service
        { showToaster: false, showLoader: !silent, useToken: true },
      );

      if (resp.status && resp.data) {
        this.story.set(resp.data);
      } else if (!silent) {
        this.hasError.set(true);
      }
    } catch {
      if (!silent) this.hasError.set(true);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Reactions
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Toggles a reaction for the current user:
   * - Same type already active → RemoveReaction (DELETE).
   * - Different type / no reaction → AddReaction (POST).
   * After the mutation, the story is silently refreshed to show updated data.
   */
  async toggleReaction(reactionType: number): Promise<void> {
    if (this.isReacting()) return;
    this.isReacting.set(true);

    try {
      if (this.currentUserReaction() === reactionType) {
        // User tapped their own active reaction — remove it
        await this.api.request<boolean>(
          'DELETE',
          `${API_ROUTES.REMOVE_REACTION}/${this.storyId}`,
          undefined,
          { showToaster: false, showLoader: false, useToken: true },
        );
      } else {
        // Add or replace the user's reaction
        const body: AddReactionRequest = { storyId: this.storyId, reactionType };
        await this.api.request<boolean>(
          'POST',
          API_ROUTES.ADD_REACTION,
          body,
          { showToaster: false, showLoader: false, useToken: true },
        );
      }

      // Close the extra reaction picker and silently refresh counts
      this.isReactionPickerOpen.set(false);
      await this.loadStory(true);
    } finally {
      this.isReacting.set(false);
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // User interaction handlers
  // ────────────────────────────────────────────────────────────────────────────

  /** Toggles the Full Transcript accordion open/closed. */
  toggleTranscript(): void {
    this.transcriptOpen.update(v => !v);
  }

  /** Toggles the secondary (Wow / Sad) reaction picker open/closed. */
  toggleReactionPicker(): void {
    this.isReactionPickerOpen.update(v => !v);
  }

  /** Navigates the user back to the previous page (Stories list). */
  goBack(): void {
    this.location.back();
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Template helpers
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Returns the correct transcript text based on the story's
   * selectedTranscriptType (0 = Verbatim, 1 = Narrative).
   * Returns an empty string if neither transcript has content.
   */
  getTranscriptText(): string {
    const s: StoryDetailData | null = this.story();
    if (!s) return '';
    if (s.selectedTranscriptType === 1) return s.transcriptNarrative?.trim() ?? '';
    return s.transcriptVerbatim?.trim() ?? '';
  }

  /**
   * Returns the total count of reactions for the given reactionType.
   * Used to drive the counter on each reaction button.
   */
  getReactionCount(type: number): number {
    return this.reactionGroups().find(g => g.type === type)?.names.length ?? 0;
  }

  /**
   * Returns a zero-padded 2-character count string for display
   * (e.g. 8 → "08", 12 → "12").
   */
  formatReactionCount(type: number): string {
    return this.getReactionCount(type).toString().padStart(2, '0');
  }

  /**
   * Returns the full names of all tagged people joined by ", ".
   * Displayed in the Story Details sidebar.
   */
  getTaggedPeopleNames(): string {
    return (this.story()?.taggedPeople ?? []).map(p => p.fullName).join(', ');
  }

  /**
   * Returns the full names of all people the story was shared with, joined by ", ".
   * Displayed in the Story Details sidebar.
   */
  getSharedWithPeopleNames(): string {
    return (this.story()?.sharedWithPeople ?? []).map((p: SharedWithPerson) => p.fullName).join(', ');
  }

  /**
   * Formats a duration in seconds to "M:SS" (e.g. 90 → "1:30").
   */
  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return '0:00';
    const m   = Math.floor(seconds / 60);
    const sec = Math.floor(seconds % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  }

  /**
   * Formats an ISO date string (e.g. "2026-05-28") to a long readable date
   * (e.g. "May 28, 2026").
   */
  formatStoryDate(dateString: string): string {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  }

  /**
   * Converts an ISO datetime string to a human-readable relative label
   * (e.g. "Today", "2 days ago", "1 week ago").
   */
  getRelativeTime(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    const diffDays = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays === 0)  return 'Today';
    if (diffDays === 1)  return '1 day ago';
    if (diffDays < 7)   return `${diffDays} days ago`;
    if (diffDays < 14)  return '1 week ago';
    if (diffDays < 30)  return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 60)  return '1 month ago';
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    if (diffDays < 730) return '1 year ago';
    return `${Math.floor(diffDays / 365)} years ago`;
  }
}

